import React, { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { AutoCompleteAddress, CustomFormData } from 'shared/types/account/Account';
import AddCircle from '@/components/customercenter-ui/svgs/addCircle';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import SuccessIcon from '@/components/customercenter-ui/svgs/notifications/success';
import ToolTips from '@/components/customercenter-ui/tooltip';
import AddressLineTooltip from '@/components/customercenter-ui/tooltip/address';
import PhoneNumberTooltip from '@/components/customercenter-ui/tooltip/phoneNumber';
import { useAccount } from '@/frontastic';
import useIntuitiveSearch from '@/frontastic/hooks/useIntuitiveSearch';
import { ErrorNotification, SuccessNotification } from '@/helpers/utils/notification';
import { compareAddresses, getCookieValue } from '@/helpers/utils/siteUtils';
import { AddressPayload, ValidateAddress } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { AddressDialogModal } from './validateAddress';
import { CONSTANTS_RESPONSE, COOKIE_KEYS, FORM_FIELD_SPECS } from '../../../../../constants/constant';
import { DEFAULT_COUNTRY, PAYLOAD_QUERY } from '../../customer-detail/constants';
import { AddressSchema } from '../../customer-detail/schema';
import { LocationInfo } from '../../order-details/types';
import { Button } from '../../ui/button';
import InputField from '../../ui/customInput/input';
import InputMaskField from '../../ui/customInputMask';
import { StateDropDown } from '../../ui/customSelect/stateDropDown';
import Loader from '../../ui/loader';
import { useToast } from '../../ui/use-toast';

const AddAddressForm = ({ customerId, Texts, AddNewAddress, getAddresses, onClose, isDialog = false }: any) => {
  const { toast } = useToast();
  const params = useSearchParams();
  const { addNewAddress, validateAddress } = useAccount();
  const [loading, setLoading] = useState(false);
  const [showadditionalAddressInfo, setShowadditionalAddressInfo] = useState(false);
  const [showCompany, setShowCompany] = useState(false);
  const [initialStateValue, setInitialStateValue] = useState('');
  const [pickListSelectedAddress, setPickListSelectedAddress] = useState<AutoCompleteAddress>();
  const [modalOpen, setModalOpen] = useState(false);
  const [formattingKey, setFormattingKey] = useState('');
  const [validateAddressResponse, setValidateAddressResponse] = useState({} as any);

  const handleShowadditionalAddressInfo = () => {
    setShowadditionalAddressInfo(true);
  };
  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    clearErrors,
    getValues,
    watch,
  } = useForm({
    resolver: zodResolver(AddressSchema),
    mode: 'all',
  });

  const firstName = watch('firstName') || '';
  const lastName = watch('lastName') || '';

  const combinedNameWarning = firstName?.length + lastName?.length >= 28 ? 'Character Limit Reached' : '';

  useIntuitiveSearch({
    setShowadditionalAddressInfo,
    setValue,
    setInitialStateValue,
    clearErrors,
    setPickListSelectedAddress,
  });

  const saveAddress = async (formData: CustomFormData) => {
    const data = formData;
    setLoading(true);
    AddNewAddress(data);
    const payload = {
      query: {
        omsCustomerId: params.get(PAYLOAD_QUERY.OMS_CUSTOMER_ID) ?? customerId,
        divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
      },
      data: {
        LocationInfo: {
          isResidential: true,
          line1: data?.streetName,
          line2: data?.additionalAddressInfo,
          city: data?.city,
          state: data?.state,
          zip: data?.postalCode,
          country: DEFAULT_COUNTRY,
          phoneNumber: data?.phone,
        },
        PersonInfo: {
          firstName: data?.firstName,
          lastName: data?.lastName,
          companyName: data?.companyName,
        },
      },
    } as AddressPayload;
    await addNewAddress(payload)
      .then(() => {
        SuccessNotification(toast, { description: 'Address Added', action: <SuccessIcon className="h-5 w-5" /> });
        getAddresses(true);
        setLoading(false);
      })
      .catch(() => {
        ErrorNotification(toast, { description: 'Error Adding Address', action: <ErrorIcon className="h-5 w-5" /> });
        setLoading(false);
      });
  };

  const onSubmit = async (data: CustomFormData) => {
    AddNewAddress(data);
    saveAddress(data);
  };

  const mergeAddressFromExactMatch = (locationInfo: LocationInfo, formValues: CustomFormData) => {
    formValues.streetName = locationInfo?.line1;
    formValues.additionalAddressInfo = locationInfo?.line2;
    formValues.city = locationInfo?.city;
    formValues.state = locationInfo?.state;
    formValues.postalCode = locationInfo?.zip;
    onSubmit(formValues);
  };

  const verifyAddress = async () => {
    setLoading(true);
    const formValues: any = getValues();
    const addressDiffers = compareAddresses(formValues, pickListSelectedAddress);
    const formData = {
      OriginalAddress: {
        LocationInfo: {
          line1: formValues?.streetName,
          line2: formValues?.additionalAddressInfo,
          city: formValues?.city,
          state: formValues?.state,
          zip: formValues?.postalCode,
          country: DEFAULT_COUNTRY,
        },
      },
    };
    if (addressDiffers) {
      const payload = {
        data: formData,
        query: {
          formattedKey: formattingKey,
          divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
        },
      } as ValidateAddress;
      const response: any = await validateAddress(payload);

      if (response && response?.code === CONSTANTS_RESPONSE.SUCCESS_CODE_OK) {
        if (response?.ValidateAddressResponse?.result === CONSTANTS_RESPONSE.VERIFIED_MATCH) {
          mergeAddressFromExactMatch(response?.ValidateAddressResponse?.ValidatedAddress?.LocationInfo, formValues);
        } else {
          const validateAddressResponse = {
            result: response?.ValidateAddressResponse?.result,
            ...response?.ValidateAddressResponse?.ValidatedAddress,
            SuggestedAddresses: response?.ValidateAddressResponse?.SuggestedAddresses,
          };
          setModalOpen(!modalOpen);
          setValidateAddressResponse(validateAddressResponse);
        }
      } else {
        handleSubmit(onSubmit)(formValues);
      }
    } else {
      handleSubmit(onSubmit)(formValues);
    }
    setLoading(false);
  };

  const mergeDataWithFormData = async (data: CustomFormData) => {
    const formData = getValues();
    const mergedData: any = { ...formData, ...data };
    const EditformData = {
      OriginalAddress: {
        LocationInfo: {
          line1: mergedData?.streetName,
          line2: mergedData?.additionalAddressInfo,
          city: mergedData?.city,
          state: mergedData?.state,
          zip: mergedData?.postalCode,
          country: DEFAULT_COUNTRY,
        },
      },
    };
    const payload = {
      data: EditformData,
      query: {
        formattedKey: formattingKey,
        divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
      },
    } as ValidateAddress;

    if (data?.isEditSelectedFromModel) {
      const response: any = await validateAddress(payload);

      if (response && response?.code === CONSTANTS_RESPONSE.SUCCESS_CODE_OK) {
        if (response?.ValidateAddressResponse?.result === CONSTANTS_RESPONSE.VERIFIED_MATCH) {
          mergeAddressFromExactMatch(response?.ValidateAddressResponse?.ValidatedAddress?.LocationInfo, mergedData);
        } else {
          const validateAddressResponse = {
            result: response?.ValidateAddressResponse?.result,
            ...response?.ValidateAddressResponse?.ValidatedAddress,
            SuggestedAddresses: response?.ValidateAddressResponse?.SuggestedAddresses,
          };
          setModalOpen(true);
          setValidateAddressResponse(validateAddressResponse);
          Object.keys(mergedData).forEach((fieldName: any) => {
            setValue(fieldName, mergedData[fieldName]);
          });
        }
      } else {
        onSubmit(mergedData);
      }
    } else {
      onSubmit(mergedData);
    }
  };

  return (
    <div
      className={isDialog ? 'custom-scrollbar max-h-[90%] max-w-[684px] overflow-y-auto bg-white sm:rounded-md' : ''}
    >
      <div>{loading && <Loader />}</div>
      {isDialog && (
        <div>
          <h2 className="text-2xl font-bold">{Texts.addNewAddress}</h2>
          <p className="text-xs font-normal">{Texts.requiredFields}</p>
        </div>
      )}
      <section>
        {modalOpen && (
          <AddressDialogModal
            openModal={modalOpen}
            setOpenModal={setModalOpen}
            values={getValues()}
            validateAddressResponse={validateAddressResponse}
            mergeDataWithFormData={mergeDataWithFormData}
            setFormattingKey={setFormattingKey}
            Texts={Texts}
          />
        )}
        <form onSubmit={handleSubmit(verifyAddress)} autoComplete="off">
          <section className="flex w-full items-baseline gap-2 px-1 pt-4">
            <section className="w-full lg:max-w-[400px]">
              <InputField
                maxLength={FORM_FIELD_SPECS.firstName.maxLength}
                items={Texts.formItemFirstName}
                id={FORM_FIELD_SPECS.firstName.id}
                htmlFor={FORM_FIELD_SPECS.firstName.id}
                register={register}
                errors={{
                  ...errors,
                  ...(combinedNameWarning &&
                    !errors?.firstName && {
                      firstName: {
                        message: combinedNameWarning,
                      },
                    }),
                }}
                label={Texts.formFirstNameLabel}
                classname="w-full"
                focusFirstName={true}
                getValues={getValues}
              />
            </section>
            <section className="w-full lg:max-w-[400px]">
              <InputField
                maxLength={FORM_FIELD_SPECS.lastName.maxLength}
                items={Texts.formItemLastName}
                id={FORM_FIELD_SPECS.lastName.id}
                htmlFor={FORM_FIELD_SPECS.lastName.id}
                register={register}
                errors={{
                  ...errors,
                  ...(combinedNameWarning &&
                    !errors?.lastName && {
                      lastName: {
                        message: combinedNameWarning,
                      },
                    }),
                }}
                label={Texts.formLastNameLabel}
                classname="w-full"
                getValues={getValues}
              />
            </section>
          </section>
          {!showCompany && (
            <button type="button" className="flex items-center gap-x-2 px-1 pt-6" onClick={() => setShowCompany(true)}>
              <AddCircle className="h-5 w-5" />
              <p className="text-base font-normal leading-5">{Texts.formItemCompanyName}</p>
              <p className="text-sm font-normal leading-5">{Texts.formOptionalItem}</p>
            </button>
          )}
          {showCompany && (
            <section className="flex w-full items-baseline px-1 pt-6">
              <section className="w-full">
                <InputField
                  items={Texts.formItemCompanyName}
                  id={FORM_FIELD_SPECS.companyName.id}
                  htmlFor={FORM_FIELD_SPECS.companyName.id}
                  register={register}
                  errors={errors}
                  label={Texts.formCompanyNameLabel}
                  maxLength={FORM_FIELD_SPECS.companyName.maxLength}
                  classname="w-full"
                />
              </section>
            </section>
          )}

          <section className="flex w-full items-baseline gap-x-2 px-1 pt-6">
            <section className="w-full cursor-pointer">
              <InputField
                items={Texts.formItemAddress}
                id={FORM_FIELD_SPECS.addressLine1.id}
                htmlFor={FORM_FIELD_SPECS.addressLine1.id}
                register={register}
                errors={errors}
                label={FORM_FIELD_SPECS.addressLine1.id}
                classname="w-full address-input"
                maxLength={FORM_FIELD_SPECS.addressLine1.maxLength}
              />
            </section>
            <ToolTips Content={{ ContentToolTip: <AddressLineTooltip /> }} />
          </section>
          {!showadditionalAddressInfo && (
            <button
              type="button"
              className="flex items-center gap-x-2 px-1 pt-6"
              onClick={handleShowadditionalAddressInfo}
            >
              <AddCircle className="h-5 w-5" />
              <p className="text-base font-normal leading-5">{Texts.formItemAdditionalAddressInfo}</p>
              <p className="text-sm font-normal leading-5">{Texts.formOptionalItem}</p>
            </button>
          )}
          <section className={`flex w-full items-baseline px-1 pt-6 ${showadditionalAddressInfo ? 'block' : 'hidden'}`}>
            <section className="w-full">
              <InputField
                items={Texts.formItemAdditionalAddressInfo}
                id={FORM_FIELD_SPECS.additionalAddressInfo.id}
                htmlFor={FORM_FIELD_SPECS.additionalAddressInfo.id}
                register={register}
                errors={errors}
                label={FORM_FIELD_SPECS.additionalAddressInfo.id}
                maxLength={FORM_FIELD_SPECS.additionalAddressInfo.maxLength}
                classname="w-full addressLine2"
              />
            </section>
          </section>
          <section className="flex w-full items-baseline px-1 pt-6">
            <section className="w-full lg:max-w-[477px]">
              <InputField
                items={Texts.formItemCity}
                id={FORM_FIELD_SPECS.city.id}
                htmlFor={FORM_FIELD_SPECS.city.id}
                register={register}
                errors={errors}
                label={FORM_FIELD_SPECS.city.id}
                classname="w-full city"
                maxLength={FORM_FIELD_SPECS.city.maxLength}
              />
            </section>
          </section>
          <section className="flex items-baseline gap-x-4 px-1 pt-6 lg:w-full">
            <section className="w-[294px]">
              <StateDropDown
                setValue={setValue}
                errors={errors}
                label={FORM_FIELD_SPECS.state.id}
                classname="w-full state"
                initialState={initialStateValue}
                register={register}
                id={FORM_FIELD_SPECS.state.id}
              />
            </section>
            <section className="w-[160px]">
              <InputField
                items={Texts.formItemZipCode}
                id={FORM_FIELD_SPECS.postalCode.id}
                htmlFor={FORM_FIELD_SPECS.postalCode.id}
                register={register}
                errors={errors}
                label={FORM_FIELD_SPECS.postalCode.id}
                classname="w-full zip"
                maxLength={FORM_FIELD_SPECS.postalCode.maxLength}
              />
            </section>
          </section>
          <section className="mb-2 flex w-full items-baseline gap-x-2 px-1 pt-6">
            <section className="w-[270px]">
              <InputMaskField
                items={Texts.formItemPhoneNumber}
                id={FORM_FIELD_SPECS.phone.id}
                htmlFor={FORM_FIELD_SPECS.phone.id}
                register={register}
                errors={errors}
                label={Texts.formPhoneNumberLabel}
                classname="w-full"
                mask={'999-999-9999'}
              />
            </section>
            <ToolTips Content={{ ContentToolTip: <PhoneNumberTooltip /> }} />
          </section>
          <section className="hidden">
            <InputField
              id={FORM_FIELD_SPECS.country.id}
              htmlFor={FORM_FIELD_SPECS.country.id}
              register={register}
              errors={errors}
              label={FORM_FIELD_SPECS.country.id}
              classname="w-full country"
              value={FORM_FIELD_SPECS.country.default}
              type={FORM_FIELD_SPECS.country.type}
            />
          </section>
          <section className="mt-6 flex justify-start gap-4">
            <Button className="rounded-md bg-deepBlue p-4 text-white">{Texts.addNewAddress}</Button>
            <Button className="p-0 underline" type="reset" onClick={onClose}>
              {Texts.cancelButton}
            </Button>
          </section>

          {isDialog && (
            <section className="mt-6 flex justify-start gap-4">
              <Button className="rounded-md bg-deepBlue p-4 text-white">{Texts.addNewAddress}</Button>
              <Button className="p-0 underline" type="reset" onClick={onClose}>
                {Texts.cancelButton}
              </Button>
            </section>
          )}
        </form>
      </section>
    </div>
  );
};

export default AddAddressForm;
